import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpark, setPolySession, type SparkVariant } from '@poly/sdk-browser/react';
import { SparkRenderer, SparkVariantSelector } from '../components/SparkRenderer';
import './Login.css';

// API endpoint for Spark auth (in Pages Functions or edge)
const AUTH_API = '';  // Empty means same origin

export default function Login() {
  const navigate = useNavigate();
  const [variant, setVariant] = useState<SparkVariant>('genesis');
  
  const { 
    state, 
    startChallenge, 
    timeRemaining,
    isAuthenticated,
    challenge,
  } = useSpark({
    variant,
    latticeUrl: AUTH_API || undefined,
    onAuthenticated: () => {
      navigate('/');
    },
  });
  
  // Memoize challengeJson to prevent infinite re-renders
  const challengeJson = useMemo(() => {
    if (!challenge) return undefined;
    return JSON.stringify({
      id: challenge.challengeId,
      device_id: '0'.repeat(64),
      nonce: challenge.challengeId.slice(0, 32),
      created_at: challenge.expiresAt - 300000,
      expires_at: challenge.expiresAt,
      spark_code: challenge.displayCode,
      session_id: challenge.sessionId,
      action: challenge.payload.action,
      service: challenge.payload.service,
    });
  }, [challenge?.challengeId]);
  
  // Post challenge to pending-challenges endpoint for mobile discovery
  useEffect(() => {
    if (challenge?.challengeId && challenge?.sessionId) {
      fetch(`${AUTH_API}/api/auth/pending-challenges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challenge_id: challenge.challengeId,
          session_id: challenge.sessionId,
          display_code: challenge.displayCode,
          expires_at: challenge.expiresAt,
          created_at: Date.now(),
          payload: btoa(JSON.stringify(challenge.payload)),
        }),
      }).catch(e => console.error('Failed to post challenge:', e));
    }
  }, [challenge?.challengeId, challenge?.sessionId]);
  
  // Poll for session status
  useEffect(() => {
    if (!challenge?.sessionId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`${AUTH_API}/api/auth/spark-status/${challenge.sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'verified' && data.session) {
            setPolySession({
              token: data.session.token,
              walletId: data.session.walletId,
              expiresAt: data.session.expiresAt,
              displayName: `Wallet ${data.session.walletId.slice(0, 8)}...`,
              roles: ['user'],
            });
            navigate('/');
          }
        }
      } catch (e) {
        console.debug('Status poll error:', e);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [challenge?.sessionId, navigate]);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  // Auto-start challenge on mount
  useEffect(() => {
    if (state.status === 'idle') {
      startChallenge();
    }
  }, []);

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <span className="logo-icon">◇</span>
            <span className="logo-text">Poly Messenger</span>
          </div>
          <h1 className="login-title">Console</h1>
          <p className="login-subtitle">
            {state.status === 'error' 
              ? 'Authentication failed. Please try again.'
              : 'Scan with Poly Messenger to sign in'}
          </p>
        </div>

        <div className="login-spark">
          {(state.status === 'pending' || state.status === 'verifying') && challengeJson && (
            <SparkRenderer
              challengeJson={challengeJson}
              variant={variant}
              size={280}
              showCode={false}
            />
          )}
          
          {state.status === 'expired' && (
            <div className="spark-expired">
              <span className="expired-icon">⏰</span>
              <span>Challenge expired</span>
              <button className="btn btn-primary" onClick={startChallenge}>
                Try Again
              </button>
            </div>
          )}
          
          {state.status === 'error' && (
            <div className="spark-error">
              <span className="error-icon">⚠️</span>
              <span>{state.error}</span>
              <button className="btn btn-primary" onClick={startChallenge}>
                Try Again
              </button>
            </div>
          )}
          
          {state.status === 'idle' && (
            <div className="spark-loading">
              <div className="spark-spinner" />
              <span>Initializing...</span>
            </div>
          )}
        </div>

        {timeRemaining !== null && timeRemaining > 0 && (
          <div className="login-timer">
            <span className="timer-label">Expires in:</span>
            <span className="timer-value">{Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}</span>
          </div>
        )}

        <div className="login-variants">
          <SparkVariantSelector
            value={variant}
            onChange={setVariant}
          />
        </div>

        <div className="login-platforms">
          <span className="platform-label">Available on:</span>
          <div className="platform-icons">
            <span className="platform-icon" title="iOS">📱 iPhone</span>
            <span className="platform-icon" title="Android">🤖 Android</span>
            <span className="platform-icon" title="Desktop">💻 Desktop</span>
          </div>
        </div>

        {/* Demo login button (for development only) */}
        <div className="login-demo">
          <button 
            className="btn btn-secondary"
            onClick={() => {
              // Demo mode - simulate successful auth
              setPolySession({
                token: 'demo-token',
                walletId: '5Vss...N9Xo',
                expiresAt: Date.now() + 24 * 60 * 60 * 1000,
                displayName: 'Demo User',
                roles: ['manager'],
              });
              navigate('/');
            }}
          >
            Demo Login (Dev Only)
          </button>
        </div>

        <div className="login-footer">
          <p>
            Don't have the app? <a href="https://polymessenger.app/download">Download Poly Messenger</a>
          </p>
        </div>
      </div>
    </div>
  );
}
